import React, { useEffect, useState } from "react";
import {
  Backdrop,
  Button,
  FormFieldSet,
  LogoLoader,
  Modal,
  showToast,
  StatusTags,
  TableAdv,
} from "../../../../../../components";
import { mapTableRows } from "../../../../../../utils";
import { bulkUpdateTalentMatch, fetchTalentMatch } from "../../../../../../redux/slices/bdmjobs/bdmjobsApi";
import { formatWords2 } from "../../../../../../utils/stringUtils";
import { totalCountStart } from "../../../../../../redux/slices/totalCount/totalCountSlice";
import { useDispatch, useSelector } from "react-redux";
import { usePagination } from "../../../../../../hooks";
import { getAllIntegrationAccess, getAllIntegrations } from "../../../../../../redux/slices/setting/settingApi";
import { addAiCallApi } from "../../../../../../redux/slices/aicalls/aicallsApi";

const Candidates = ({ jobPostId, jobDetails }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const [talentMatchData, setTalentMatchData] = useState();
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isScheduleAiCalls, setIsScheduleAiCalls] = useState(false);
  const [numberOfCalls, setNumberOfCalls] = useState();
  const [showError, setShowError] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [integrationAccess, setIntegrationAccess] = useState();
  const [botRecruiter, setBotRecruiter] = useState();
  const [isNextClicked, setIsNextClicked] = useState(false);

  const { page, limit, setPage, handleLimitChange } = usePagination( 10, (newPage, newLimit) => { } );
  const { value } = useSelector((state) => state.totalCount);
  const totalCount = value?.aiRecruiterTMCount?.data || 0;

  const columns = [
    {
      key: "candidateName",
      label: "Candidate Name",
      columnMenu: false,
    },
    {
      key: "email",
      label: "Email ID",
    },
    {
      key: "phone",
      label: "Phone number",
    },
    {
      key: "jobTitle",
      label: "Job Title",
    },    
    {
      key: "aiCallInitiated",
      label: "AI Call Initiated",
      columnMenu: false,
      render: (row) => <StatusTags tag={row.aiCallInitiated} />,
    },
    {
      key: "experience",
      label: "Experience (In years)",
    },
    {
      key: "workAuthorization",
      label: "Work Authorization",
    },
    {
      key: "location",
      label: "Location",
    },
    {
      key: "relevance",
      label: "Relevance",
      render: (row) => <div className="text-info">{row?.relevance}</div>,
    },
  ];

  const formattedRows = talentMatchData?.map((item, index) => ({
    id: item?.uiid,
    candidateName: `${item?.candidateId?.firstName} ${item?.candidateId?.lastName}`.trim(),
    email:
      typeof item?.candidateId?.email === "string"
        ? item?.candidateId?.email
        : item?.candidateId?.email?.primary?.email || "N/A",
    phone: `${item.candidateId?.mobile?.primary?.code} ${item?.candidateId?.mobile?.primary?.number}`,
    jobTitle: formatWords2(item?.candidateId?.jobTitle) || "N/A",
    experience: item?.candidateId?.exp ? `${item?.candidateId?.exp?.year || 0}y ${item?.candidateId?.exp?.mon || 0}m` : "N/A",
    workAuthorization: item?.candidateId?.workAuth ? formatWords2(item?.candidateId?.workAuth) : "N/A",
    location: (() => {
      const loc = item?.candidateId?.location;
      const formatted = [loc?.city, loc?.state].filter(Boolean).join(", ");
      return formatted || "N/A";
    })(),
    candidateId: item?.candidateId?.uiid,
    jobPostingId: item?.jobPostId?.uiid,
    relevance: item.skillMatchPercent ? item.skillMatchPercent + "%" : "N/A",
    aiCallInitiated: item?.isAiCallInitiated === true ? "Yes" : item?.isAiCallInitiated === false ? "No" : null
  }));

  const mappedRows = mapTableRows(formattedRows, columns)

  const fetchTalentMatches = async () => {
    try {
      setLoading(true);
      const res = await fetchTalentMatch({
        coll: "TalentMatche",
        pg: page,
        lim: limit,
        fil: { jobPostId: {type: "NONE", value: jobPostId} },
        ret: "uiid candidateId jobPostId skillMatchPercent isAiCallInitiated",
        ref: "JobPosting Candidate",
        extret: "uiid firstName",
        incl: [
          "jobInfo.workAuth jobInfo.location uiid",
          "uiid firstName lastName email mobile salaryInfo.payRate jobTitle exp workAuth location",
        ],
        sort: { skillMatchPercent: -1 },
      });

      const data = res?.data?.data || [];

      setTalentMatchData(data);
    } catch (err) {
      console.error("Error fetching Talent Match:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleAiCall = () => {
    if (selectedRows?.length) {
      const selectedData = talentMatchData?.filter((data) => 
        selectedRows.includes(data?.uiid) && (data?.isAiCallInitiated === false || !data?.isAiCallInitiated)
      ) || [];
      setSelectedCandidates(selectedData);
    }
    setIsScheduleAiCalls(true)
  };

  const handleNext = async () => {
    if (!numberOfCalls || numberOfCalls <= 0) {
      setShowError(true);
      return;
    }
    setShowError(false);

    try {
      setLoading(true);
      const res = await fetchTalentMatch({
        coll: "TalentMatche",
        pg: 1,
        lim: numberOfCalls,
        fil: { 
          jobPostId: {type: "NONE", value: jobPostId},
          isAiCallInitiated: {type: "NONE", value: false }
        },
        ret: "uiid candidateId jobPostId skillMatchPercent isAiCallInitiated",
        ref: "JobPosting Candidate",
        extret: "uiid firstName",
        incl: [
          "jobInfo.workAuth jobInfo.location uiid",
          "uiid firstName lastName email mobile salaryInfo.payRate jobTitle exp workAuth location",
        ],
        sort: { skillMatchPercent: -1 },
      });

      if (res?.success) {
        const data = res?.data?.data || [];

        if (!data?.length) {
          showToast(`No Candidates available for AI Calls`, "warning");
          return;
        }

        setSelectedCandidates(data);
      }
      setIsNextClicked(true);
    } catch (err) {
      console.error("Error fetching Talent Match:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsScheduleAiCalls(false);
    setNumberOfCalls();
    setShowError(false);
    setSelectedCandidates([]);
    setSelectedRows([]);
    setIsNextClicked(false);
  };

  const handleRemoveCandidate = (removeId) => {
    const updatedCandidates = selectedCandidates.filter(candidate => candidate?.uiid !== removeId);
    const updatedRows = selectedRows.filter(id => id !== removeId);
    setSelectedRows(updatedRows);
    setSelectedCandidates(updatedCandidates);
  };

  const handleInitiateAiCalls = async () => {
    if (selectedCandidates?.length > integrationAccess?.accessConfig?.credits) {
      showToast(`Insufficient credits. You can only initiate ${integrationAccess?.accessConfig?.credits} calls.`, "error", 3000);
      return;
    }

    const buildPayload = selectedCandidates?.map((candidate) => ({
      candidateId: candidate?.candidateId?.uiid,
      jobPostId: Number(jobPostId),
      source: 1,
      talentMatchId: candidate?.uiid,
      integrationId: integrationAccess?.integrationId,
      addedBy: user?.data?.uiid,
      userId: botRecruiter?.uiid,
    }));

    const aiCallResponse = await addAiCallApi({
      coll: "AiCall",
      bulkPost: true,
      addedBy: user?.data?.uiid,
      accessId: integrationAccess?.uiid,
      payload: buildPayload
    });

    if (aiCallResponse?.success) {
      const talentMatchIds = selectedCandidates?.map(c => c?.uiid) || [];

      const payload = [
        {
          type: "UPDATEMANY",
          value: { fil: {uiid: {type: "IN", value: talentMatchIds}}, type: "SET", value: {isAiCallInitiated: true} },
        },
      ];

      const res = await bulkUpdateTalentMatch({
        coll: "TalentMatche",
        payload,
      });

      showToast("AI Calls initiated successfully", "success");
      handleCloseModal();
      fetchTalentMatches();
    } else {
      showToast("Failed to initiate AI Calls", "error");
    }

  };

  const fetchIntegratiuonAccess = async () => {
    try {
      const notifyTo = jobDetails?.notifyTo || [];
      const botUser = notifyTo?.find(user => user?.userType === 2);
      setBotRecruiter(botUser);

      const getIntegrations = await getAllIntegrations({
        coll: "Integration",
        fil: JSON.stringify({ category: { type: "NONE", value: 6 }, type: { type: "NONE", value: 2 }, status: {type: "NONE", value: 1} }),
        ret: "uiid name category type status"
      });

      if (getIntegrations?.success) {
        const integrationIds = getIntegrations?.data?.data?.map(i => i.uiid) || [];

        if (integrationIds?.length) {
          const getIntegrationAccess = await getAllIntegrationAccess({
            coll: "IntegrationAccess",
            fil: {
              integrationId: { type: "IN", value: integrationIds },
              userId: { type: "NONE", value: botUser?.uiid },
              status: { type: "NONE", value: 1 }
            }
          });

          if (getIntegrationAccess?.success) {
            const data = getIntegrationAccess?.data?.data || [];

            const selected = data.reduce((best, current) => {
              const bestCredits = best?.accessConfig?.credits ?? -1;
              const currentCredits = current?.accessConfig?.credits ?? -1;

              if (currentCredits > bestCredits) return current;

              if (currentCredits === bestCredits) {
                return current.uiid > best.uiid ? current : best;
              }

              return best;
            }, data[0]);

            if (selected) {
              setIntegrationAccess(selected);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching integrations: ", error);
    }
  };

  useEffect(() => {
    dispatch(
      totalCountStart({
        collection: "talentmatches",
        coll: "TalentMatche",
        tname: "aiRecruiterTMCount",
        app: "ats",
        filters: { jobPostId: {type: "NONE", value: jobPostId} },
      })
    );
    fetchIntegratiuonAccess();
  }, []);

  useEffect(() => {
    fetchTalentMatches();
  }, [limit, page, totalCount]);

  return (
    <div className="flex flex-col mt-5 overflow-auto">
      <div className="flex flex-grow overflow-auto gap-4 mx-0.5">
        <TableAdv
          tableId={"jobs_aijobdetails_candidates_table"}
          showCheckbox
          columns={columns}
          rows={mappedRows}
          totalCount={totalCount}
          serverPagination={true}
          page={page}
          limit={limit}
          onPaginationChange={setPage}
          onLimitChange={handleLimitChange}
          onSelectionChange={(row) => setSelectedRows(row)}
          defaultSelectedRows={selectedRows}
          toolbarRight={{ 
            fullScreen: true,
            add: [{
              component: (
                <Button
                  title={"Schedule AI Call"}
                  size="sm"
                  onClick={() => handleScheduleAiCall()}
                />
            )}]
          }}
        />
      </div>
      
      <Modal
        isOpen={isScheduleAiCalls}
        title={"Schedule AI Calls"}
        titleColor="primary"
        onClose={() => handleCloseModal(false)}
        width={"max-w-2xl"}
      >
        {!selectedCandidates?.length ? (
            <div className="flex flex-col gap-3">
              <p className="text-center">Select Candidates from the table to Initiate AI Calls</p>
              <p className="text-md text-center">(OR)</p>
              <FormFieldSet
                type="text"
                title="Total Number of Calls"
                value={numberOfCalls}
                onChange={(value) => {
                    const numericValue = Number(value);
                    if (!/^\d+$/.test(numericValue)) return;
                    // if (numericValue <= 0) {
                    //   showToast("Value should be at least 1", "warning", 2000);
                    // } else 
                    if (numericValue > totalCount) {
                      setNumberOfCalls(totalCount);
                      showToast(`Maximum of ${totalCount} candidates available. You are not allowed to schedule more calls.`, "warning", 4000);
                    } else {
                      setNumberOfCalls(numericValue);
                    }
                }}
                placeholder="Total Number of Calls"
                error={showError ? "Please enter a valid number of calls" : ""}
              />
              {isNextClicked ? (
                <p className="text-danger italic text-sm">No Candidates Eligible for Scheduling AI Calls</p>
              ) : selectedRows?.length ? (
                <p className="text-danger italic text-sm">Selected candidates are not Eligible for Scheduling AI Calls</p>
              ) : null}
              <div className="flex justify-end gap-4">
                <Button
                  title="Cancel"
                  variant="outlined"
                  onClick={() => handleCloseModal(false)}
                />
                <Button title="Next" onClick={() => {handleNext()}} />
              </div>        
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <p>Initiating AI Calls to 
                <span className="text-primary font-semibold ms-1">{selectedCandidates?.length}</span> Candidates.
              </p>
              {selectedCandidates?.length > integrationAccess?.accessConfig?.credits && (
                <p className="text-danger italic text-sm">
                  {`${botRecruiter?.firstName} ${botRecruiter?.lastName} only have ${integrationAccess?.accessConfig?.credits} credits. 
                  You can initiate maximum of ${integrationAccess?.accessConfig?.credits} calls.`}
                </p>
              )}
              <div className="grid grid-cols-2 gap-3">
                {selectedCandidates?.map((candidate, index) => (
                  <div key={index} className="bg-gray-100 p-2 border border-gray-300 rounded flex flex-col gap-2">
                    <div className="flex justify-between items-center">
                      <p>{candidate?.candidateId?.firstName} {candidate?.candidateId?.lastName}</p>
                      <Button 
                        type="icon"
                        icon={"delete"}
                        variant="text"
                        onClick={() => handleRemoveCandidate(candidate?.uiid)}
                      />
                    </div>
                    <hr className="text-gray-300" />
                    <div className="text-sm ">
                      <p className="text-primary">Job Title</p>
                      <p>{candidate?.candidateId?.jobTitle}</p>
                    </div>
                    <div className="grid grid-cols-2">
                      <div className="text-sm ">
                        <p className="text-primary">Mobile Number</p>
                        <p>{`${candidate?.candidateId?.mobile?.primary?.code} ${candidate?.candidateId?.mobile?.primary?.number}`}</p>
                      </div>
                      <div className="text-sm ">
                        <p className="text-primary">Relevance</p>
                        <p>{`${candidate?.skillMatchPercent || 0}%`}</p>
                      </div>
                    </div>               
                  </div>
                ))}
              </div>
              <div className="flex justify-end items-center gap-2">
                <Button 
                  title={"Cancel"}
                  variant="outlined"
                  onClick={() => handleCloseModal()}             
                />
                <Button 
                  title={"Submit"} 
                  onClick={() => handleInitiateAiCalls()}              
                />
              </div>
            </div>
          )          
        }
      </Modal>

      <Backdrop isOpen={loading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default Candidates;
